import { db } from '../db/indexeddb';
import { shouldMakeAPICall } from '../utils/network';
import axios from 'axios';
import { API_ENDPOINT } from '../../services/api/resources';

/**
 * Purchase Request Repository
 * Handles all PR data operations with offline-first approach
 */
export class PRRepository {
  /**
   * Get all PRs from IndexedDB
   */
  async getAllFromCache() {
    try {
      const prs = await db.purchaseRequests.toArray();
      return prs.map(pr => pr.data);
    } catch (error) {
      console.error('Error reading PRs from cache:', error);
      return [];
    }
  }

  /**
   * Save PRs to IndexedDB
   */
  async saveToCache(prs) {
    try {
      const optimisticCount = prs.filter(pr => pr._pendingSync || pr._lastUpdated).length;
      
      await db.transaction('rw', db.purchaseRequests, async () => {
        // Clear existing data
        await db.purchaseRequests.clear();
        
        // Insert new data - IMPORTANT: Preserve all fields including _pendingSync and _lastUpdated
        const prData = prs.map(pr => ({
          pr_token: pr.pr_token || '',
          pr_code: pr.pr_code || '',
          data: pr,  // Store the entire PR object including _pendingSync, _lastUpdated, etc.
          lastSynced: Date.now(),
        }));
        
        await db.purchaseRequests.bulkAdd(prData);
      });
      
      // Verify the save preserved optimistic updates
      const verifyCache = await this.getAllFromCache();
      const verifyOptimistic = verifyCache.filter(pr => pr._pendingSync || pr._lastUpdated);
      
      console.log(`✅ Cached ${prs.length} PRs (${optimisticCount} with optimistic updates)`);
      if (optimisticCount > 0) {
        console.log(`✅ Verified: ${verifyOptimistic.length} optimistic updates persisted in IndexedDB`);
        if (verifyOptimistic.length !== optimisticCount) {
          console.warn(`⚠️ Warning: Expected ${optimisticCount} optimistic updates, but only ${verifyOptimistic.length} found after save!`);
        }
      }
    } catch (error) {
      console.error('Error saving PRs to cache:', error);
    }
  }

  /**
   * Get PR by token
   */
  async getByToken(prToken) {
    try {
      const pr = await db.purchaseRequests
        .where('pr_token')
        .equals(prToken)
        .first();
      return pr?.data || null;
    } catch (error) {
      console.error('Error getting PR by token:', error);
      return null;
    }
  }

  /**
   * Get PR by code
   */
  async getByCode(prCode) {
    try {
      const pr = await db.purchaseRequests
        .where('pr_code')
        .equals(prCode)
        .first();
      return pr?.data || null;
    } catch (error) {
      console.error('Error getting PR by code:', error);
      return null;
    }
  }

  /**
   * Fetch from API and sync to cache
   */
  async syncFromAPI(authToken, userToken, orgId) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      console.warn('⚠️ PR sync skipped - offline or not authenticated');
      return this.getAllFromCache();
    }

    try {
      const isUser = false; // Determine from context
      const requestData = isUser
        ? { user_token: userToken }
        : { org_id: orgId };

      const response = await axios({
        url: API_ENDPOINT.get_saved_prs,
        method: 'POST',
        data: requestData,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = response?.data || [];
      
      // IMPORTANT: Preserve optimistic updates (PRs with _pendingSync flag)
      const existingCache = await this.getAllFromCache();
      const optimisticPRs = existingCache.filter(pr => pr._pendingSync || pr._lastUpdated);
      
      console.log(`🔄 Syncing from fallback API. Found ${optimisticPRs.length} optimistic updates in cache`);
      
      const cacheMap = new Map(data.map(pr => [pr.pr_token, pr]));
      
      // Keep PRs with pending sync (optimistic updates) - these take priority over API data
      optimisticPRs.forEach(pr => {
        console.log(`📱 Preserving optimistic update for PR: ${pr.pr_code || pr.pr_token}`);
        // Overwrite API data with optimistic update
        cacheMap.set(pr.pr_token, pr);
      });
      
      const mergedPRs = Array.from(cacheMap.values());
      await this.saveToCache(mergedPRs);
      
      const preservedCount = mergedPRs.filter(pr => pr._pendingSync || pr._lastUpdated).length;
      console.log(`✅ Synced ${mergedPRs.length} PRs (preserved ${preservedCount} optimistic updates)`);
      return mergedPRs;
    } catch (error) {
      console.error('Error syncing PRs from API:', error);
      // Return cached data on error
      return this.getAllFromCache();
    }
  }

  /**
   * Get last sync time
   */
  async getLastSyncTime() {
    try {
      const pr = await db.purchaseRequests.orderBy('lastSynced').last();
      return pr?.lastSynced || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get individual PR details from cache (for edit/view)
   */
  async getIndividualPRFromCache(prToken) {
    try {
      const cacheKey = `pr_individual_${prToken}`;
      const cached = await db.cache.get(cacheKey);
      return cached?.data || null;
    } catch (error) {
      console.error('Error reading individual PR from cache:', error);
      return null;
    }
  }

  /**
   * Save individual PR details to cache
   */
  async saveIndividualPRToCache(prToken, prData) {
    try {
      const cacheKey = `pr_individual_${prToken}`;
      await db.cache.put({
        key: cacheKey,
        data: prData,
        lastSynced: Date.now(),
      });
      console.log(`✅ Cached individual PR details for ${prToken}`);
    } catch (error) {
      console.error('Error saving individual PR to cache:', error);
    }
  }

  /**
   * Fetch individual PR from API and sync to cache
   */
  async syncIndividualPRFromAPI(authToken, prToken) {
    if (!shouldMakeAPICall(authToken, '')) {
      return this.getIndividualPRFromCache(prToken);
    }

    try {
      const response = await axios({
        method: 'POST',
        url: API_ENDPOINT.purchase_request_individual_items_view,
        data: { pr_token: prToken },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const prData = response?.data?.response;
      if (prData) {
        await this.saveIndividualPRToCache(prToken, prData);
      }
      return prData;
    } catch (error) {
      console.error('Error syncing individual PR from API:', error);
      // Return cached data on error
      return this.getIndividualPRFromCache(prToken);
    }
  }

  /**
   * Transform combined API response to match expected format
   * Adds missing fields: buyer_name, approvers[].name, pr_approval_status, approvers[].status
   */
  transformCombinedPRData(prs) {
    if (!Array.isArray(prs)) {
      return [];
    }

    return prs.map(pr => {
      // Add buyer_name from buyer_user
      const buyer_name = pr.buyer_user
        ? `${pr.buyer_user.first_name || ''} ${pr.buyer_user.last_name || ''}`.trim()
        : '';

      // Transform approvers: add name and status
      const approvers = (pr.approvers || []).map(approver => ({
        ...approver,
        name: `${approver.first_name || ''} ${approver.last_name || ''}`.trim(),
        status: approver.status || 'Pending', // Default to Pending if missing
      }));

      // Calculate pr_approval_status from approvers
      let pr_approval_status = 'Pending';
      if (approvers.length > 0) {
        const allApproved = approvers.every(a => a.status === 'Approved');
        const anyRejected = approvers.some(
          a => a.status === 'Rejected' || a.status === 'Cancelled'
        );
        
        if (anyRejected) {
          pr_approval_status = 'Rejected';
        } else if (allApproved) {
          pr_approval_status = 'Approved';
        } else {
          pr_approval_status = 'Pending';
        }
      }

      return {
        ...pr,
        buyer_name,
        pr_approval_status,
        approvers,
        // Preserve offline sync flags
        _pendingSync: pr._pendingSync,
        _lastUpdated: pr._lastUpdated,
        _tempToken: pr._tempToken,
      };
    });
  }

  /**
   * Fetch combined PRs from API and sync to cache
   * This API returns all PRs with full item details in one call
   */
  async syncCombinedPRsFromAPI(authToken, userToken, orgId) {
    if (!shouldMakeAPICall(authToken, userToken)) {
      console.warn('⚠️ Combined PR sync skipped - offline or not authenticated');
      return this.getAllFromCache();
    }

    try {
      const isUser = false; // Determine from context if needed
      const requestData = isUser
        ? { user_token: userToken }
        : { org_id: orgId };

      const response = await axios({
        url: API_ENDPOINT.combined_saved_pr_with_items_view,
        method: 'POST',
        data: requestData,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      // Response structure: { purchase_requests: [...], count: 228 }
      const purchaseRequests = response?.data?.purchase_requests || [];
      
      // Transform data to add missing fields
      const transformedPRs = this.transformCombinedPRData(purchaseRequests);
      
      // IMPORTANT: Preserve optimistic updates (PRs with _pendingSync flag)
      const existingCache = await this.getAllFromCache();
      const optimisticPRs = existingCache.filter(pr => pr._pendingSync || pr._lastUpdated);
      
      console.log(`🔄 Syncing from API. Found ${optimisticPRs.length} optimistic updates in cache:`, 
        optimisticPRs.map(pr => ({ 
          pr_token: pr.pr_token, 
          pr_code: pr.pr_code,
          _pendingSync: pr._pendingSync,
          _lastUpdated: pr._lastUpdated 
        }))
      );
      
      const cacheMap = new Map(transformedPRs.map(pr => [pr.pr_token, pr]));
      
      // Keep PRs with pending sync (optimistic updates) - these take priority over API data
      optimisticPRs.forEach(pr => {
        console.log(`📱 Preserving optimistic update for PR: ${pr.pr_code || pr.pr_token}`, {
          hasPendingSync: !!pr._pendingSync,
          lastUpdated: pr._lastUpdated,
          request_reason: pr.request_reason?.substring(0, 50) + '...'
        });
        // Overwrite API data with optimistic update
        cacheMap.set(pr.pr_token, pr);
      });
      
      const mergedPRs = Array.from(cacheMap.values());
      
      // Verify optimistic updates are still in merged data
      const preservedCount = mergedPRs.filter(pr => pr._pendingSync || pr._lastUpdated).length;
      console.log(`✅ Merged ${mergedPRs.length} PRs, preserved ${preservedCount} optimistic updates`);
      
      // Save merged data to cache
      await this.saveToCache(mergedPRs);
      
      // Verify after save
      const verifyCache = await this.getAllFromCache();
      const verifyOptimistic = verifyCache.filter(pr => pr._pendingSync || pr._lastUpdated);
      console.log(`✅ After save: ${verifyCache.length} PRs in cache, ${verifyOptimistic.length} with optimistic updates`);
      
      return mergedPRs;
    } catch (error) {
      console.error('Error syncing combined PRs from API:', error);
      // Return cached data on error
      return this.getAllFromCache();
    }
  }
}

export const prRepository = new PRRepository();
